import { template5PlaceholderData, TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { forwardRef, useEffect, useRef } from 'react';
import Menu from './components/menu';
import { BoxComponent } from '../../../base/box/Box';
import { useNormalizedPixels } from '../../../hooks/useResize';
import { getScaleSize } from '../../../utils/utils';
import Media from '../Media';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template5 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, scale = 1, style, development, timezone, active, shouldLoad = true } = props;
  const mainRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  const { normalizePixels } = useNormalizedPixels();
  useFont(template?.fontFamily);
  useEffect(() => {
    if (mainRef.current) {
      sizeRef.current = {
        width: mainRef.current.offsetWidth,
        height: mainRef.current.offsetHeight,
      };
      mainRef.current.style.backgroundSize = `${sizeRef.current.width}px ${sizeRef.current.height}px`;
    }
  }, [style?.width, style?.height]);

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }

    return {
      backgroundImage: `url(https://dashboard-demo-assests.s3.us-west-2.amazonaws.com/dashboard/menuboard/Template5bg.webp)`,
    };
  };

  const { content, ...rest } = template;
  const menu =
    Array.isArray(template?.content) && template?.content?.length > 0 ? template?.content[0] : template5PlaceholderData;
  const isBrandLogo = template?.brandLogo && template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const isLayoutOne = isMedia && template.layoutType === 1;
  const isLayoutTwo = isMedia && template.layoutType === 2;

  return (
    <div
      ref={mainRef}
      style={{
        overflow: 'hidden',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        padding: isLayoutOne
          ? '0px'
          : `${getScaleSize(normalizePixels(40, 'height'), scale)} 
            ${getScaleSize(normalizePixels(20, 'width'), scale)}`,
        ...bgStyles(),
        ...(!style ? { height: '100vw', width: '100vh' } : style),
      }}
    >
      {isBrandLogo && (
        <BoxComponent
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            height: getScaleSize(normalizePixels(70, 'height'), scale),
            padding: isLayoutOne
              ? `${getScaleSize(normalizePixels(0, 'height'), scale)} 
                ${getScaleSize(normalizePixels(20, 'width'), scale)}
                `
              : '0px',
            margin: isLayoutOne
              ? `${getScaleSize(normalizePixels(40, 'height'), scale)} 
                ${getScaleSize(normalizePixels(0, 'width'), scale)}
                ${getScaleSize(normalizePixels(0, 'height'), scale)} 
                ${getScaleSize(normalizePixels(0, 'width'), scale)} 
                `
              : `${getScaleSize(normalizePixels(0, 'height'), scale)} 
                ${getScaleSize(normalizePixels(0, 'width'), scale)}
                ${getScaleSize(normalizePixels(40, 'height'), scale)} 
                ${getScaleSize(normalizePixels(0, 'width'), scale)} 
                `,
          }}
        >
          <img
            src={template?.brandLogo?.image as string}
            alt="Brand logo"
            style={{ objectFit: 'contain', height: '100%' }}
          />
        </BoxComponent>
      )}
      <div
        ref={ref}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          rowGap: getScaleSize(normalizePixels(24, 'height'), scale),
          alignContent: 'space-between',
          columnGap: '4%',
          padding: isLayoutOne
            ? `${getScaleSize(normalizePixels(40, 'height'), scale)} 
              ${getScaleSize(normalizePixels(40, 'width'), scale)} 
              ${template.taxLabel.isEnabled ? getScaleSize(normalizePixels(30, 'height'), scale) : '0px'} 
              ${getScaleSize(normalizePixels(40, 'width'), scale)} 
              `
            : `0px 0px ${template.taxLabel.isEnabled ? getScaleSize(normalizePixels(50, 'height'), scale) : '0px'}`,
        }}
      >
        <div
          id="columnSeparator"
          style={{
            position: 'absolute',
            height: '95%',
            left: '50%',
            marginTop: '5%',
            borderRight: '0.25px solid #24252899',
          }}
        />
        <Menu menu={menu} template={rest} development={development} timezone={timezone} scale={scale} />
        {template.taxLabel.isEnabled && (
          <BoxComponent
            sx={{
              position: 'absolute',
              left: isLayoutOne ? getScaleSize(normalizePixels(40, 'width'), scale) : '0px',
              bottom: '0%',
            }}
          >
            <TypographyComponent
              sx={{ fontWeight: '500', fontSize: normalizePixels(12, 'height') }}
              variant="subtitle1"
            >
              {template.taxLabel.taxText}
            </TypographyComponent>
          </BoxComponent>
        )}
        {isLayoutTwo && (
          <div
            id="template-5-layout-2-media"
            style={{
              width: '48%',
              position: 'absolute',
              bottom: '0',
              right: '0%',
              height: getScaleSize(normalizePixels(500, 'height'), scale),
            }}
          >
            <Media
              media={media}
              height={getScaleSize(normalizePixels(500, 'height'), scale)}
              playing={active}
              shouldLoad={shouldLoad}
            />
          </div>
        )}
      </div>
      {isLayoutOne && (
        <div
          style={{
            marginTop: getScaleSize(normalizePixels(30, 'height'), scale),
            height: getScaleSize(normalizePixels(390, 'height'), scale),
          }}
        >
          <Media
            isDevelopment={development}
            media={media}
            height={'100%'}
            borderRadius="0px"
            playing={active}
            shouldLoad={shouldLoad}
          />
        </div>
      )}
    </div>
  );
});
